import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '../../libraries/tailwind/utils'; // Replace with your utility for class concatenation
import Icon from '../renders/icon/Icon';
import ICONS from '../../enums/icons';
import Entity from '../renders/entity/Entity';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const Select = React.forwardRef(({
    options = [],
    placeholder,
    onChange,
    value,
    namespace,
    disabled = false,
    prefixEntity = '',
    className, icon,
    noValidateIcon = false,
    dataCy
}, ref) => {
    const { t: translate } = useTranslation();
    let placeholderTranslated = translate('select.default');
    if (placeholder) {
        placeholderTranslated = namespace ? translate(placeholder, { ns: namespace }) : translate(placeholder);
    }

    return (
        <SelectPrimitive.Root value={value} onValueChange={onChange}>
            <SelectPrimitive.Trigger
                ref={ref}
                className={cn(
                    'flex items-center justify-between h-10 px-4 py-2 w-full rounded border border-gray-300 bg-white text-sm text-gray-700',
                    'focus:outline-none focus:ring-1 focus:ring-gray-200 focus:border-gray-=200 ',
                    'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-300',
                    className
                )}
                disabled={disabled}
                data-cy={dataCy}
            >
                {
                    icon && <Icon icon={ICONS.FILTER} className={''} />
                }
                <SelectPrimitive.Value placeholder={placeholderTranslated} />
                <SelectPrimitive.Icon>
                    <Icon icon={ICONS.CHEVRON_DOWN} className='h-4 w-4 text-gray-500' />
                </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>

            <SelectPrimitive.Content
                position='popper'
                side='bottom'
                sideOffset={4}
                className={cn(
                    'bg-white border border-gray-200 rounded shadow-md z-50',
                    'max-h-60 overflow-auto w-[var(--radix-select-trigger-width)]'
                )}
            >
                <SelectPrimitive.ScrollUpButton className='flex items-center justify-center h-8 text-gray-500'>
                    <Icon icon={ICONS.CHEVRON_UP} className='h-4 w-4' />
                </SelectPrimitive.ScrollUpButton>
                <SelectPrimitive.Viewport className='p-2'>
                    {options.map((option) => (
                        <SelectPrimitive.Item
                            data-cy={option.value}
                            key={option.value}
                            value={option.value}
                            className={cn(
                                'relative flex items-center px-8 py-2 text-sm text-gray-700 cursor-pointer select-none rounded-md',
                                'focus:bg-gray-200 focus:text-black',
                                noValidateIcon && 'pl-2'
                            )}
                        >
                            {
                                !noValidateIcon && <SelectPrimitive.ItemIndicator className='absolute left-2'>
                                    <Icon icon={ICONS.VALIDATE} className='h-2 w-2 text-success/40' />
                                </SelectPrimitive.ItemIndicator>
                            }
                            <SelectPrimitive.ItemText>
                                <Entity nowrap entity={`${prefixEntity}${option.name}`} namespace={option.namespace ?? namespace} />
                            </SelectPrimitive.ItemText>
                        </SelectPrimitive.Item>
                    ))}
                </SelectPrimitive.Viewport>
                <SelectPrimitive.ScrollDownButton className='flex items-center justify-center h-8 text-gray-500'>
                    <Icon icon={ICONS.CHEVRON_DOWN} className='h-4 w-4' />
                </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
        </SelectPrimitive.Root>
    );
});

Select.propTypes = {
    options: PropTypes.array,
    placeholder: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.any,
    namespace: PropTypes.string,
    disabled: PropTypes.bool,
    prefixEntity: PropTypes.string,
    className: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    noValidateIcon: PropTypes.bool,
    dataCy: PropTypes.string
};

export default Select;
